import { env } from "@/env";
import { useEffect, useRef } from "react";

const SCRIPT_ID = "cf-turnstile-script";
const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js";

interface TurnstileApi {
  render: (el: HTMLElement, opts: TurnstileOptions) => string;
  remove: (widgetId: string) => void;
  reset: (widgetId?: string) => void;
}

interface TurnstileOptions {
  sitekey: string;
  callback: (token: string) => void;
  "expired-callback"?: () => void;
  "error-callback"?: () => void;
  theme?: "light" | "dark" | "auto";
  size?: "normal" | "compact" | "flexible";
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

/** True když je Turnstile nakonfigurován → formulář vyžaduje token pro odeslání. */
export const turnstileRequired = Boolean(env.VITE_TURNSTILE_SITE_KEY);

function loadTurnstileScript(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.turnstile) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("turnstile-script-error")), {
        once: true,
      });
      if (window.turnstile) resolve();
      return;
    }
    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("turnstile-script-error"));
    document.head.appendChild(script);
  });
}

interface TurnstileWidgetProps {
  /** Voláno s tokenem po úspěšném ověření, nebo `undefined` při vypršení/chybě. */
  onToken: (token: string | undefined) => void;
  testId?: string;
}

/**
 * Cloudflare Turnstile widget pro veřejné formuláře. Renderuje se POUZE když je
 * nastaven `VITE_TURNSTILE_SITE_KEY`; jinak nevykreslí nic (server pak ověření
 * také přeskakuje). Vzhled je laděný na tmavý veřejný web.
 */
export function TurnstileWidget({ onToken, testId = "turnstile-widget" }: TurnstileWidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  // Drží aktuální callback, aby se efekt nespouštěl při každém renderu.
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;

  const siteKey = env.VITE_TURNSTILE_SITE_KEY;

  useEffect(() => {
    const el = containerRef.current;
    if (!siteKey || !el) return;
    let cancelled = false;

    loadTurnstileScript()
      .then(() => {
        if (cancelled || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(el, {
          sitekey: siteKey,
          theme: "dark",
          callback: (token) => onTokenRef.current(token),
          "expired-callback": () => onTokenRef.current(undefined),
          "error-callback": () => onTokenRef.current(undefined),
        });
      })
      .catch(() => {
        // Skript se nenačetl (např. blokovaný) — formulář necháme použitelný,
        // server stejně ověřuje a bez tokenu odeslání odmítne.
      });

    return () => {
      cancelled = true;
      const id = widgetIdRef.current;
      if (id && window.turnstile) {
        try {
          window.turnstile.remove(id);
        } catch {
          // widget už mohl být odstraněn — ignorujeme.
        }
      }
      widgetIdRef.current = null;
    };
  }, [siteKey]);

  if (!siteKey) return null;
  return <div ref={containerRef} data-testid={testId} className="min-h-[65px]" />;
}
